import loadable from '@loadable/component';
import {
    root,
    IAppConfig,
    ICachedComponentsRegistry,
    IComponentRegistry,
    IComponentViewController,
    IServiceRegistryComponent,
    RegisteredComponent,
    AllServicesTypes,
    IManifestServiceRegistryComp
} from 'elc-universal-utils';
import { ELCLogger, TriggerType } from 'elc-logging';
import { ServiceBusController } from './ServiceBusController';
import { getServices } from './utils';

export type ServiceInitializer = (config: IAppConfig) => ServiceBusController | void;

root.ServiceRegistry = root.ServiceRegistry || {};

export abstract class Service {
    protected logger: ELCLogger;

    public serviceBusController: ServiceBusController;
    public componentsRegistry: IComponentRegistry = {};
    public cachedComponentsRegistry: ICachedComponentsRegistry = {};
    public isLazy: boolean = false;
    public isStarted: boolean = false;
    public isStarting: boolean = false;
    public views: string[] = [];

    constructor(
        public name: string,
        public version: string,
        public serviceInitializer: ServiceInitializer
    ) {}

    public async start(appConfig: IAppConfig): Promise<AllServicesTypes | void> {
        this.isStarting = true;
        this.logger =
            this.logger ||
            new ELCLogger({
                serviceName: this.name,
                environment: appConfig.env || root.env,
                buid: appConfig.businessUnitId || root.buid,
                serviceVersion: this.version
            });

        try {
            if (!this.isStarted) {
                await this.serviceInitializer(appConfig);
                this.isStarted = true;
            }
        } catch (error) {
            this.logger.error({
                message: `Could not start service ${this.name}`,
                triggerType: TriggerType.render,
                payload: {
                    error,
                    context: {
                        serviceRegistry: root.ServiceRegistry
                    }
                }
            });
        }

        return root.ServiceRegistry[this.name];
    }

    public register = (overrideProperties?: Omit<IManifestServiceRegistryComp, 'start'>) => {
        root.ServiceRegistry = getServices();

        root.ServiceRegistry[this.name] = Object.assign(
            root.ServiceRegistry[this.name] || {},
            {
                start: this.start,
                name: this.name,
                version: this.version,
                views: this.views,
                getView: this.getView,
                registerView: this.registerView,
                setViewInCache: this.setViewInCache,
                getViewFromCache: this.getViewFromCache,
                serviceInitializer: this.serviceInitializer,
                componentsRegistry: this.componentsRegistry,
                cachedComponentsRegistry: this.cachedComponentsRegistry,
                isStarted: this.isStarted,
                isLazy: this.isLazy,
                isStarting: this.isStarting
            } as IServiceRegistryComponent,
            overrideProperties || {}
        );
    };

    public getView = (componentName: string) => {
        return this.componentsRegistry[componentName] || null;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public setViewInCache = (componentName: string, component: React.ComponentType<any>) => {
        this.cachedComponentsRegistry[componentName] = component;
    };

    public getViewFromCache = (componentName: string) => {
        return this.cachedComponentsRegistry[componentName] || null;
    };

    public registerView = (
        componentName: string,
        component: RegisteredComponent,
        viewController?: () => IComponentViewController
    ) => {
        this.views.push(componentName);

        this.componentsRegistry[componentName] = {
            componentName,
            component: loadable(component),
            viewController
        };
    };
}
